// Filtro de período compartilhado do Analytics Comercial — inclui os períodos calendário-
// alinhados (trimestre/semestre/ano) e o período personalizado, além dos já existentes.
import { mesUnicoDeIntervalo } from './fechamentoMensal.ts';

export type Periodo =
    | 'tudo' | 'ontem' | 'hoje' | '7d' | '14d' | '30d'
    | 'mes_atual' | 'mes_anterior' | 'trimestre' | 'semestre' | 'ano' | 'personalizado';

export interface CustomRange {
    inicio: string; // YYYY-MM-DD
    fim: string;    // YYYY-MM-DD
}

export const PERIODOS: Array<{ value: Periodo; label: string }> = [
    { value: 'tudo', label: 'Todo o período' },
    { value: 'ontem', label: 'Ontem' },
    { value: 'hoje', label: 'Hoje' },
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '14d', label: 'Últimos 14 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: 'mes_atual', label: 'Mês atual' },
    { value: 'mes_anterior', label: 'Mês anterior' },
    { value: 'trimestre', label: 'Este trimestre' },
    { value: 'semestre', label: 'Este semestre' },
    { value: 'ano', label: 'Este ano' },
    { value: 'personalizado', label: 'Período personalizado' },
];

const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
const endOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate(), 23, 59, 59, 999);

/** Janela do período selecionado. `null` para 'tudo' (sem limite) e para 'personalizado'
 *  sem range ainda escolhido pelo usuário. */
export function resolvePeriodoRange(p: Periodo, custom?: CustomRange): { start: Date; end: Date } | null {
    const now = new Date();
    switch (p) {
        case 'tudo': return null;
        case 'ontem': {
            const ontem = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
            return { start: startOfDay(ontem), end: endOfDay(ontem) };
        }
        case 'hoje': return { start: startOfDay(now), end: now };
        case '7d': return { start: new Date(now.getTime() - 7 * 86400000), end: now };
        case '14d': return { start: new Date(now.getTime() - 14 * 86400000), end: now };
        case '30d': return { start: new Date(now.getTime() - 30 * 86400000), end: now };
        case 'mes_atual': return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
        case 'mes_anterior': {
            const ini = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const fim = new Date(now.getFullYear(), now.getMonth(), 1);
            return { start: ini, end: new Date(fim.getTime() - 1) };
        }
        case 'trimestre': {
            const trimestreIni = Math.floor(now.getMonth() / 3) * 3;
            return { start: new Date(now.getFullYear(), trimestreIni, 1), end: now };
        }
        case 'semestre': {
            const semestreIni = now.getMonth() < 6 ? 0 : 6;
            return { start: new Date(now.getFullYear(), semestreIni, 1), end: now };
        }
        case 'ano': return { start: new Date(now.getFullYear(), 0, 1), end: now };
        case 'personalizado': {
            if (!custom || !custom.inicio || !custom.fim) return null;
            return { start: new Date(custom.inicio + 'T00:00:00'), end: new Date(custom.fim + 'T23:59:59.999') };
        }
        default: return null;
    }
}

/** Mesmo comprimento de janela do período atual, imediatamente anterior a ela — usado
 *  para comparação/tendência no bloco de Visão Geral. `null` quando não há comparação
 *  aplicável ('tudo', ou 'personalizado' sem range). */
export function resolvePeriodoAnteriorRange(p: Periodo, custom?: CustomRange): { start: Date; end: Date } | null {
    const now = new Date();
    switch (p) {
        case 'tudo': return null;
        case 'mes_atual': {
            const ini = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const fim = new Date(now.getFullYear(), now.getMonth(), 1);
            return { start: ini, end: new Date(fim.getTime() - 1) };
        }
        case 'mes_anterior': {
            const ini = new Date(now.getFullYear(), now.getMonth() - 2, 1);
            const fim = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            return { start: ini, end: new Date(fim.getTime() - 1) };
        }
        case 'trimestre': {
            const trimestreIni = Math.floor(now.getMonth() / 3) * 3;
            const ini = new Date(now.getFullYear(), trimestreIni - 3, 1);
            const fim = new Date(now.getFullYear(), trimestreIni, 1);
            return { start: ini, end: new Date(fim.getTime() - 1) };
        }
        case 'semestre': {
            const semestreIni = now.getMonth() < 6 ? 0 : 6;
            const ini = new Date(now.getFullYear(), semestreIni - 6, 1);
            const fim = new Date(now.getFullYear(), semestreIni, 1);
            return { start: ini, end: new Date(fim.getTime() - 1) };
        }
        case 'ano': {
            const ini = new Date(now.getFullYear() - 1, 0, 1);
            const fim = new Date(now.getFullYear(), 0, 1);
            return { start: ini, end: new Date(fim.getTime() - 1) };
        }
        default: {
            // Janelas móveis (ontem, hoje, 7d, 14d, 30d, personalizado): desloca a mesma
            // duração pra trás, terminando um instante antes do início da janela atual.
            const atual = resolvePeriodoRange(p, custom);
            if (!atual) return null;
            const duracaoMs = atual.end.getTime() - atual.start.getTime();
            const end = new Date(atual.start.getTime() - 1);
            const start = new Date(end.getTime() - duracaoMs);
            return { start, end };
        }
    }
}

export function inPeriodo(iso: string, p: Periodo, custom?: CustomRange): boolean {
    if (p === 'tudo') return true;
    const range = resolvePeriodoRange(p, custom);
    if (!range) return true; // 'personalizado' sem datas escolhidas ainda — não filtra
    const d = new Date(iso);
    return d >= range.start && d <= range.end;
}

/** Quando o período selecionado corresponde a exatamente um mês-calendário — usado pelo
 *  bloco de Meta x Realizado, que usa metas mensais (src/lib/metasComerciais.ts), pra decidir se
 *  faz sentido mostrar meta pro período escolhido. */
export function periodoResolvesToSingleMonth(p: Periodo, custom?: CustomRange): { mes: number; ano: number } | null {
    const now = new Date();
    if (p === 'mes_atual') return { mes: now.getMonth() + 1, ano: now.getFullYear() };
    if (p === 'mes_anterior') {
        const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return { mes: d.getMonth() + 1, ano: d.getFullYear() };
    }
    if (p === 'personalizado' && custom?.inicio && custom?.fim) {
        return mesUnicoDeIntervalo(custom.inicio, custom.fim);
    }
    return null;
}
